import { Close } from "@mui/icons-material";
import { alpha, IconButton } from "@mui/material";
import {
  CopyOutline,
  FileTrayFullOutline,
  InformationCircleOutline,
  MoonOutline,
  PartlySunnyOutline,
  ShieldCheckmark,
  SunnyOutline,
} from "@raresail/react-ionicons";
import { App, Tag, Tooltip } from "antd";
import { Children, Fragment, isValidElement } from "react";
import styled from "styled-components";
import { PRIMARY_COLOR, PRIMARY_LIGHT } from "../../constants/colors";
import {
  ACTIVE,
  COMPLETED,
  DEFAULTED,
  FAILED,
  LATE,
  PAID,
  PENDING,
} from "../../constants/constants";
import { getDisplayName, getLoanGrade, stringify } from "../../utils/helpers";
import { useAuth } from "../context/AuthContext";

export const ErrorText = ({ children }: { children?: any }) => {
  if (children) return <p className="font11 red">{children}</p>;
  return null;
};

export const HelperText = ({ text }: { text?: string }) => {
  if (text) return <p className="font11 grey thin">{text}</p>;
  return null;
};

export const FormLabel = ({
  children,
  required = false,
  info,
  ...props
}: {
  children: any;
  required?: any;
  info?: any;
}) => {
  const { optional }: any = props || {};
  return (
    <p className="font13 textDark flexNullCenter gap5 capitalize">
      {children} {required && <span className="red">*</span>}
      {info && (
        <Tooltip title={info}>
          <InformationCircleOutline className="main" style={{ fontSize: 17 }} />
        </Tooltip>
      )}
      {optional && <span className="grey thin font12">(optional)</span>}
    </p>
  );
};

export const EmptyField = () => <i style={{ color: "#aaa" }}>N/A</i>;

export const EmptyView = ({ source }: { source?: string }) => (
  <div className="flexCenter">
    <div className="flexColumn flexCenter gap10 grey p20">
      <FileTrayFullOutline fontSize={40} />
      <div className="textCenter">
        <div>No Results</div>
        <div className="font11">
          {source ? `No ${stringify(source)} available` : "Nothing to show"}
        </div>
      </div>
    </div>
  </div>
);

export function getStatusColor(status: string) {
  switch (status?.toLowerCase()) {
    case PENDING:
      return "gold";

    case ACTIVE:
    case COMPLETED:
      return "green";

    case PAID:
      return "green-inverse";

    case DEFAULTED:
    case FAILED:
    case LATE:
      return "red";

    default:
      break;
  }
}

export const StatusTag = ({
  status,
  icon: Icon,
}: {
  status: string;
  icon?: any;
}) => {
  return (
    <Tag
      className="font11 capitalize"
      color={getStatusColor(status)}
      icon={Icon && <Icon style={{ marginRight: 5, marginBottom: -2 }} />}
      style={{ margin: 0 }}
    >
      {stringify(status)}
    </Tag>
  );
};

export const IconBox = ({
  icon: Icon,
  size = 24,
}: {
  icon: any;
  size?: number;
}) => {
  return (
    <div className="r15 lightBg main p15 flexCenter">
      <Icon fontSize={size} />
    </div>
  );
};

export const Greeting = () => {
  const { user } = useAuth();

  const getGreeting = () => {
    const hour = new Date().getHours();
    let text = "Good Evening",
      icon = MoonOutline;

    if (hour < 12) {
      text = "Good Morning";
      icon = PartlySunnyOutline;
      return { text, icon };
    }
    if (hour < 18) {
      text = "Good Afternoon";
      icon = SunnyOutline;
      return { text, icon };
    }

    return { text, icon };
  };

  const { text, icon } = getGreeting();

  return (
    <div className="flexNullCenter gap15 whiteBg p15 r10">
      <div className="flexGrow">
        <div className="flexNullCenter gap10 mb5">
          <div className="font16 textDark bold">{text}!</div>

          {/* TAG */}
          <SmallTag icon={ShieldCheckmark}>{user?.user_type}</SmallTag>
        </div>

        <div className="font12 grey">{getDisplayName(user)}</div>
      </div>

      <IconBox icon={icon} />
    </div>
  );
};

export const SmallTag = ({
  children,
  icon: Icon,
}: {
  children?: any;
  icon?: any;
}) => (
  <div
    className="capitalize font11 lightBg r7 main flexNullCenter gap5"
    style={{ padding: "4px 8px" }}
  >
    {Icon && <Icon />}
    <div>{children}</div>
  </div>
);

export const AnimateChildren = ({
  children,
}: {
  children: any;
  startIndex?: number;
}) => {
  // Pass-through. The original AOS fade-up animation was hiding menu
  // items below the fold (e.g. borrowers couldn't see "My Account"
  // because the CreditProfileCard pushed it past the viewport, so AOS
  // never triggered and items stayed at opacity:0). Rendering children
  // directly makes the app feel instantly responsive — items appear as
  // soon as the user scrolls. To restore the fade later, swap this
  // return for the commented block below.
  return children;

  // ── Previous AOS-based stagger (kept for easy revert) ──
  // return Children.map(children, (child, i) => {
  //   if (!child) return null;
  //   if (isValidElement(child) && child.type === Fragment) {
  //     return (
  //       <AnimateChildren startIndex={i}>
  //         {(child as any).props.children}
  //       </AnimateChildren>
  //     );
  //   }
  //   const index = i + startIndex;
  //   return (
  //     <div
  //       key={index}
  //       data-aos="fade-up"
  //       data-aos-duration="1000"
  //       data-aos-offset={index * -10}
  //       data-aos-delay={index * 100}
  //     >
  //       {child}
  //     </div>
  //   );
  // });
};

export const Heading = ({
  children,
  subtitle,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { subtitle?: string }) => (
  <div className="flexGrow">
    <div
      {...rest}
      className={`semiBold textDark capitalize ${rest.className || ""}`}
    >
      {typeof children === "string" ? stringify(children) : children}
    </div>
    {subtitle && <div className="font12 grey lineNormal">{subtitle}</div>}
  </div>
);

export const SmallButton = ({
  icon: Icon,
  children,
  onClick,
  suffix,
}: {
  icon?: any;
  children: any;
  onClick?: any;
  suffix?: any;
}) => {
  return (
    <div
      className="font12 flexNullCenter gap10 main lightBg r7 pointer"
      onClick={onClick}
      style={{ padding: "4px 8px" }}
    >
      <Icon /> {children}
      {/* SUFFIX */}
      {suffix}
    </div>
  );
};

export const BottomSheetTitle = ({
  children,
  dismiss,
}: {
  children: any;
  dismiss?: any;
}) => (
  <div>
    <div className="flexSpaceCenter mb5">
      <Heading>{children}</Heading>
      <IconButton onClick={dismiss}>
        <Close fontSize="small" />
      </IconButton>
    </div>
    <div className="lightBg" style={{ height: 1.2 }} />
  </div>
);

export const CounterView = ({
  value = 0,
  size = 18,
}: {
  value: number;
  size?: number;
}) =>
  value > 0 ? (
    <div
      className="r5 mainBg white semiBold flexCenter font12"
      style={{ width: size, height: size }}
    >
      {value}
    </div>
  ) : null;

export const ListItemWrapper = styled.div`
  &:hover {
    margin: -10px;
    padding: 10px;
    border-radius: 10px;
    cursor: pointer;
    background-color: ${PRIMARY_LIGHT};
    transition: 0.3s ease;
  }
`;

export const Divider = () => (
  <div style={{ height: 1, backgroundColor: "#e9eefd" }} />
);

export const InfoBox = ({
  color = PRIMARY_COLOR,
  children,
}: {
  color?: string;
  children: any;
}) => {
  const variantColor = alpha(color, 0.05);
  return (
    <div
      className="font11 p15 r10"
      style={{
        color,
        border: `1px solid ${variantColor}`,
        background: variantColor,
        lineHeight: 1.3,
      }}
    >
      {children}
    </div>
  );
};

export const RiskLevelTag = ({ value }: { value: any }) => {
  const { label, color } = getLoanGrade(value);
  return (
    <div
      className="font11 white r5 semiBold"
      style={{ background: color, padding: "4px 8px" }}
    >
      {label}
    </div>
  );
};

export const CopyTextField = ({
  children,
  value,
}: {
  children: any;
  value?: any;
}) => {
  const { message } = App.useApp();

  return (
    <div className="flexNullCenter gap5">
      {/* TEXT */}
      <div>{children}</div>

      {/* BUTTON */}
      <IconButton
        color="inherit"
        size="small"
        onClick={() => {
          if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(value || children);
            message.info("Copied to clipboard");
          }
        }}
      >
        <CopyOutline fontSize={16} />
      </IconButton>
    </div>
  );
};
